import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { addQuantity } from "~/features/addToCart/redux/cartAmounts/actions";
import { type AddToCartItem, type AddToCartPartItem, sendAddToCart } from "~/features/cart/cart/api/AddToCart";
import { zero, zeroNetPrice } from "~/utils/PriceUtils";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { type RootState } from "~/app/store";
import { addMessageAdvanced } from "~/features/toasts/redux/actions";
import {
  getNetPrice,
  getPriceInfo,
  getSurcharge,
  getTariff,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import { type AddToCartFormData } from "~/features/addToCart/hooks/useAddToCartNew";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { selectProperAmountInCart } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { sendAddToQuote } from "~/features/cart/activeQuote/api/addToQuote";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { getStore } from "~/app/store/getStore";
import { TranslationType } from "~/common/types/TranslationModel";
import { batch } from "react-redux";
import type { AddToCartSource } from "~/app/analytics/types";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

function prepareApiItemThunk(
  newItemData: AddToCartFormData & { productCode: string; rdd?: Date },
  isRestricted: boolean,
  context?: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  },
): ThunkResult<AddToCartItem> {
  return (dispatch, getState) => {
    const state = getState();
    const productCode = newItemData.productCode;
    const qty = newItemData.quantity;

    const priceReference = newItemData.priceReference;

    const descriptor = selectProductDescriptor(state, {
      productCode,
      priceReference,
    });
    // If we didn't have this information already available, we wouldn't be able to go through
    // the ATC popup flow in the first place, so we can assume it's available

    const baseInfo = descriptor.base;

    const packageCode = newItemData.selectedPackCode;
    const selectedPackage = newItemData.selectedPack;
    const packageSize = selectedPackage?.packSize ?? 1;
    const qtyInCart = selectProperAmountInCart({
      productCode,
      packCode: packageCode,
      priceReference,
    })(state);
    const totalQuantity = qtyInCart + qty;
    const selectPriceProps = { product: descriptor, packCode: packageCode, totalQuantity };
    const netPrice = getNetPrice(selectPriceProps) ?? zero();
    const surcharge = getSurcharge(selectPriceProps);
    const tariffSurchargeApplicable = getTariff(selectPriceProps.product);

    const priceInfo = getPriceInfo({ product: descriptor, packCode: packageCode });
    const is15DigitsPrice = descriptor.isMultiPrice;

    const optionalParts: AddToCartPartItem[] = Object.values(newItemData.optionalParts)
      .filter((item) => item.selected)
      .map((item) => {
        const partProductDescriptor = ProductService.getProductDescriptor(item.productId, priceReference).current;
        const packaging = ProductService.findPackagingByCode(partProductDescriptor, item.selectedPackCode);
        const fifteenDigit = partProductDescriptor.base.fifteenDigit[item.selectedPackCode];

        return {
          productCode: item.productId,
          packageCode: item.selectedPackCode,
          netPrice: item.netPrice?.value ?? 0,
          priceUnit: item.priceUnit,
          packageSize: packaging.packSize,
          quantity: item.quantity,
          manualUpdated: item.isQtyUserModified,
          primaryWarehouseCode: packaging.primaryWarehouseCode,
          partTypeCode: ProductService.findOptionalPartById(descriptor, item.productId)!.type,
          surcharge: item.surcharge,
          tariffSurchargeApplicable: item.tariffSurcharge,
          originCountry: fifteenDigit?.countryOfOrigin ?? "",
        };
      });

    const netPriceValue = netPrice?.value ?? 0;
    let salesNetPriceValue: number | undefined = undefined;
    let salesCampaignCode: string | undefined = undefined;
    let saleEnabled = false;
    if (newItemData.salePriceEnabled) {
      const saleData = calculateSalesCampaign4PDP({ productDescriptor: descriptor, includeSoldOut: true });
      const saleForPack = saleData.data?.salePerPack[packageCode];
      if (saleForPack?.salesStock) {
        // If item is NOT sold out, we add it with sales campaign data
        salesCampaignCode = saleForPack?.campaignCode;
        salesNetPriceValue = saleForPack?.actualPrice?.value;
        saleEnabled = true;
      }
    }

    const fifteenDigit = baseInfo.fifteenDigit[packageCode];

    const apiItem: AddToCartItem = {
      productCode: baseInfo.hybrisCode,
      packageSize,
      packageCode,
      quantity: newItemData.quantity,
      comment: newItemData.comment ?? "",
      netPrice: netPriceValue,
      priceUnit: priceInfo?.priceUnit ?? 1,
      selectedDeliveryDate: newItemData.rdd ?? null,
      selectedDeliveryDateIsManual: !!newItemData.rdd,
      primaryWarehouseCode: selectedPackage?.primaryWarehouseCode ?? "",
      fifteenDigit: !!is15DigitsPrice,
      priceReference: priceReference,
      isPMA: isProductPMA(descriptor),

      restrictedPackage: isRestricted,
      optionalParts,
      surcharge,
      position: newItemData.position,
      tariffSurchargeApplicable,
      originCountry: fifteenDigit?.countryOfOrigin ?? "",
      salesCampaignCode: saleEnabled ? salesCampaignCode : undefined,
      saleNetPrice: saleEnabled ? salesNetPriceValue : undefined,
      specialPrice: saleEnabled,

      recommendationPlace: context?.recommendationPlace,
      recommendationType: context?.recommendationType,
    };

    return apiItem;
  };
}

function sendAddToCartRequestAction(apiItem: AddToCartItem | AddToCartItem[]): ThunkResult<Promise<boolean>> {
  return (dispatch, getState) => {
    const items = Array.isArray(apiItem) ? apiItem : [apiItem];

    let promise;
    if (userIsSalesman()) {
      promise = sendAddToQuote(items.map((item) => ({ ...item, selectedDeliveryDate: new Date() })));
    } else {
      promise = sendAddToCart(items);
    }
    return promise
      .then(() => {
        batch(() => {
          items.forEach((item) => {
            dispatch(addQuantity(item.productCode, item.packageCode, item.quantity, item.priceReference));
          });
        });
        return true;
      })
      .catch(() => {
        return false;
      });
  };
}

export async function bulkAddToCartApi({
  items,
  restrictionQuery,
  showSuccessDrawer = true,
  context,
}: {
  items: (AddToCartFormData & { productCode: string; rdd?: Date })[];
  restrictionQuery: string;
  showSuccessDrawer?: boolean;
  context: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  };
}) {
  const dispatch = getStore().dispatch;

  const apiItems = items.map((item) => {
    const cartData: AddToCartFormData & { productCode: string; saleNetPrice?: number } = item;
    const sapNo = ProductService.getBaseProductData(item.productCode)?.sapMaterialNumber;
    const isRestricted = !!(sapNo && restrictionQuery?.includes(sapNo) && isPackageRestrictionEnabled());

    const apiItem: AddToCartItem = dispatch(prepareApiItemThunk(cartData, isRestricted, context));
    return apiItem;
  });

  const isRecommendation = !!context.recommendationType && !!context.recommendationPlace;

  AnalyticsMQ.instance.push("addToCart", {
    products: items.map((item) => ({
      product: ProductService.getBaseProductData(item.productCode)!,
      packaging: {
        code: item.selectedPackCode,
        size: item.selectedPack?.packSize ?? 1,
      },
      price: item.netPrice ?? undefined,
      quantity: item.quantity,
    })),
    context: {
      source: "unknown",
      recommendationPlace: isRecommendation ? context.recommendationPlace : undefined,
      recommendationType: isRecommendation ? context.recommendationType : undefined,
    },
  });

  const result = await dispatch(sendAddToCartRequestAction(apiItems));

  const cartData: (AddToCartFormData & { productCode: string; saleNetPrice?: number })[] = items;
  cartData.forEach((formData, index) => {
    const apiItem = apiItems[index];
    // This code duplicates logic from sendAddToCartRequestAction. TODO: Refactor later
    if (formData.salePriceEnabled && apiItem.saleNetPrice && apiItem.saleNetPrice <= apiItem.netPrice) {
      if (formData.netPrice) {
        formData.netPrice = {
          ...formData.netPrice,
          value: apiItem.netPrice,
        };
      }
      formData.saleNetPrice = apiItem.saleNetPrice;
    }
  });

  if (result) {
    if (showSuccessDrawer) {
      openModal("add-to-cart-dialog", (close) => (
        <CartSuccessModal formData={cartData} onClose={close} page={context.page} />
      ));
    }
  } else {
    const totalQuantity = cartData.reduce((acc, item) => acc + item.quantity, 0);
    getStore().dispatch(
      addMessageAdvanced("error", {
        type: TranslationType.INTERNAL,
        value: "addToCart.failure",
        params: { count: totalQuantity },
      }),
    );
  }
  return result;
}

export async function addToCartApi({
  restrictionQuery,
  showSuccessDrawer = true,
  onSuccessCallback,
  context,
  ...formData
}: AddToCartFormData & {
  productCode: string;
  restrictionQuery: string;
  showSuccessDrawer?: boolean;
  position?: number;
  onSuccessCallback?: () => void;
  addToCartSource: AddToCartSource;
  context: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  };
}) {
  const cartData: AddToCartFormData & { productCode: string; saleNetPrice?: number } = formData;
  const product = ProductService.getBaseProductData(formData.productCode)!;
  const sapNo = product.sapMaterialNumber;
  const isRestricted = !!(sapNo && restrictionQuery?.includes(sapNo) && isPackageRestrictionEnabled());

  const apiItem: AddToCartItem = getStore().dispatch(prepareApiItemThunk(formData, isRestricted, context));

  const isRecommendation = !!context.recommendationType && !!context.recommendationPlace;

  AnalyticsMQ.instance.push("addToCart", {
    products: [
      {
        product,
        price: formData.netPrice ?? {
          ...zeroNetPrice(product),
        },
        quantity: apiItem.quantity,
        packaging: {
          code: apiItem.packageCode,
          size: apiItem.packageSize ?? 1,
        },
      },
    ],
    context: {
      source: formData.addToCartSource,
      recommendationPlace: isRecommendation ? context.recommendationPlace : undefined,
      recommendationType: isRecommendation ? context.recommendationType : undefined,
    },
  });

  const result = await getStore().dispatch(sendAddToCartRequestAction(apiItem));

  // This code duplicates logic from sendAddToCartRequestAction. TODO: Refactor later
  if (cartData.salePriceEnabled && apiItem.saleNetPrice && apiItem.saleNetPrice <= apiItem.netPrice) {
    cartData.saleNetPrice = apiItem.saleNetPrice;
  }

  if (result) {
    if (showSuccessDrawer) {
      openModal("add-to-cart-dialog", (close) => (
        <CartSuccessModal formData={[cartData]} onClose={close} page={context.page} />
      ));
    }
    onSuccessCallback?.();
  } else {
    getStore().dispatch(
      addMessageAdvanced("error", {
        type: TranslationType.INTERNAL,
        value: "addToCart.failure",
        params: { count: formData.quantity },
      }),
    );
  }
  return result;
}
